import { ChangeDetectionStrategy, Component, DestroyRef, effect, ElementRef, OnDestroy, OnInit, signal, viewChild } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { first, Observable } from 'rxjs';
import { DropdownComponent, ItemSelectedEvent } from '../../dropdown/dropdown.component';
import { BomItem } from '../../../models/bom-item.model';
import { Route } from '../../../utils/route';
import { select } from '../../../utils/select';
import { formatAmount } from '../../../utils/format-amount';
import { formatDayMonthYear } from '../../../utils/date-only';
import { isExactBomItemMatch } from '../../../utils/exact-match';
import { ClientOrderState, ClientOrderStore } from './client-order.store';
import { AllocationVm, ClientOrderItemVm, isFrozen, remainingAmount, ShipmentLineVm, unshippedAmount } from './models/client-order.view-model';
import { ShipmentListItemDto } from '../shipments/models/shipment-list-item.dto';
import { ClientOrderHeader } from './models/client-order.dto';
import { ClientOrderTreeNodeComponent } from './components/client-order-tree-node/client-order-tree-node.component';
import { ClientOrderFlowComponent } from './components/client-order-flow/client-order-flow.component';
import { ContextMenuComponent, ContextMenuItem } from '../../context-menu/context-menu.component';
import { AddNodeDropdownComponent, BomItemSelected } from '../../add-node-dropdown/add-node-dropdown.component';
import { ItemPhotoComponent } from '../../item-photo/item-photo.component';

type HeaderField = keyof ClientOrderHeader;

@Component({
  selector: 'client-order-page',
  templateUrl: './client-order.page.html',
  styleUrl: './client-order.page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe, RouterLink, DropdownComponent, ClientOrderTreeNodeComponent, ClientOrderFlowComponent, ContextMenuComponent, AddNodeDropdownComponent,
    ItemPhotoComponent
  ]
})
export class ClientOrderPage implements OnInit, OnDestroy {
  // the BOM tree's menu, verbatim — customizing a snapshot is the same two gestures
  readonly contextMenuItems: ContextMenuItem[] = [
    { id: 'add-child', label: 'Dodaj element' },
    { id: 'remove-node', label: 'Usuń element' }
  ];

  readonly state$: Observable<ClientOrderState>;

  // the header of an order that does not exist yet — lives here until "Utwórz" sends it
  readonly draft = signal<ClientOrderHeader>({ clientName: '', name: null, deadline: null });

  // double-click edits, like the root amounts — one header field at a time
  readonly editingField = signal<HeaderField | undefined>(undefined);
  readonly fieldInput = viewChild<ElementRef<HTMLInputElement>>('fieldInput');

  // "Utwórz wysyłkę" turns into a one-field prompt for the transport (0015 Decisions)
  readonly creatingShipment = signal(false);
  readonly transportInput = viewChild<ElementRef<HTMLInputElement>>('transportInput');

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private store: ClientOrderStore,
    private destroyRef: DestroyRef
  ) {
    this.state$ = this.store.state$;

    // the input only exists while editing — focus it as soon as it appears (overseer report-input precedent)
    effect(() => {
      const input = this.fieldInput() ?? this.transportInput();

      if (input) {
        input.nativeElement.focus();
        input.nativeElement.select();
      }
    });
  }

  ngOnInit(): void {
    this.route.paramMap.pipe(first(), takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const id = Route.parseId(params.get('id'));

      this.store.init(id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();

      this.initParams();
    });
  }

  ngOnDestroy(): void {
    this.store.destroy();
  }

  // --- new order ---

  updateDraft(field: HeaderField, value: string): void {
    this.draft.update(draft => ({ ...draft, [field]: normalize(field, value) }));
  }

  canCreate(): boolean {
    return this.draft().clientName.trim().length > 0;
  }

  create(): void {
    if (!this.canCreate()) {
      return;
    }

    this.store.createOrder(this.draft()).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  // --- existing order header ---

  startEditing(field: HeaderField): void {
    this.editingField.set(field);
  }

  stopEditing(): void {
    this.editingField.set(undefined);
  }

  saveField(field: HeaderField, value: string, current: ClientOrderHeader): void {
    this.stopEditing();

    const header = { ...current, [field]: normalize(field, value) };

    if (!header.clientName) {
      return;
    }

    this.store.patchHeader(header).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  // --- confirmation (0016): "Potwierdź" / "Cofnij potwierdzenie" ---

  confirm(): void {
    this.store.confirm().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  unconfirm(): void {
    this.store.unconfirm().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  // --- positions ---

  searchBomItems = (searchTerm: string) => this.store.searchBomItems(searchTerm);

  formatBomItem = (item: BomItem) => item.name;

  isExactBomItemMatch = isExactBomItemMatch;

  onPositionSelected(event: ItemSelectedEvent<BomItem>): void {
    // the create row (+ Utwórz „…”) on a name the catalog does not know creates the BOM item first (JIT BOM, 0110)
    const add = event.item
      ? this.store.addPosition(event.item.id, event.item.name, event.item.unit)
      : this.store.createBomItemAndAddPosition(event.searchTerm.trim());

    add.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  // --- pegging: coverage, unlink, "Utwórz zlecenie" ---

  isFrozen = isFrozen;

  formatAmount = formatAmount;
  formatDayMonthYear = formatDayMonthYear;

  remainingAmount = remainingAmount;

  hasRemaining(positions: ClientOrderItemVm[]): boolean {
    return positions.some(position => remainingAmount(position) > 0);
  }

  unlink(position: ClientOrderItemVm, allocation: AllocationVm): void {
    this.store.deleteAllocation(position.id, allocation.allocationId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  createProductionOrder(): void {
    this.store.createProductionOrderFromRemaining()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(orderId => this.router.navigate(['/orders', orderId]));
  }

  // --- shipping (0015): the third link, "Utwórz wysyłkę", "Dodaj do wysyłki" ---

  unshippedAmount = unshippedAmount;

  hasUnshipped(positions: ClientOrderItemVm[]): boolean {
    return positions.some(position => unshippedAmount(position) > 0);
  }

  // one dropdown per position: the closure keeps the position's id, the store reads its current lines
  searchPlannedShipmentsFor = (position: ClientOrderItemVm) => (searchTerm: string) => this.store.searchPlannedShipments(position.id, searchTerm);

  formatShipment = (shipment: ShipmentListItemDto) => `${shipment.shipmentNumber} · ${shipment.transport}`;

  onShipmentSelected(position: ClientOrderItemVm, event: ItemSelectedEvent<ShipmentListItemDto>): void {
    // Enter on a raw search term selects nothing — a wysyłka is picked, never typed in
    if (!event.item) {
      return;
    }

    this.store.addToShipment(position.id, event.item).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  removeFromShipment(position: ClientOrderItemVm, line: ShipmentLineVm): void {
    this.store.removeFromShipment(position.id, line).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  startCreatingShipment(): void {
    this.creatingShipment.set(true);
  }

  cancelCreatingShipment(): void {
    this.creatingShipment.set(false);
  }

  createShipment(transport: string): void {
    // Transport is required — an empty Enter keeps the prompt open
    if (!transport.trim()) {
      return;
    }

    this.cancelCreatingShipment();

    this.store.createShipmentFromUnshipped(transport.trim())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(shipmentId => this.router.navigate(['/shipments', shipmentId]));
  }

  // --- customizing position trees ---

  onContextMenuAction(actionId: string): void {
    const contextMenu = this.store.state.contextMenu!;

    if (actionId === 'add-child') {
      this.store.openAddNodeDropdown(contextMenu.nodeId, contextMenu.position);
    }

    if (actionId === 'remove-node') {
      // delete runs at page level, so the request survives the node component being destroyed by the tree update
      this.store.deleteNode(contextMenu.nodeId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    }
  }

  onContextMenuClose(): void {
    this.store.closeContextMenu();
  }

  onAddNodeBomItemSelected(item: BomItemSelected): void {
    this.store.addChildFromDropdownItem(item).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  onAddNodeDropdownClose(): void {
    this.store.closeAddNodeDropdown();
  }

  private initParams(): void {
    this.state$.pipe(select(state => state.order?.id), takeUntilDestroyed(this.destroyRef)).subscribe(orderId => {
      if (!orderId) {
        return;
      }

      this.router.navigate(['../', orderId], {
        relativeTo: this.route,
        queryParamsHandling: 'merge',
        replaceUrl: true
      });
    });
  }
}

// optional fields travel as null, never as an empty string
function normalize(field: HeaderField, value: string): string | null {
  const trimmed = value.trim();

  if (field === 'clientName') {
    return trimmed;
  }

  return trimmed.length > 0 ? trimmed : null;
}
